/**
 * The `wikiLink` node and the `[[` suggester (`031-content-format.md`, `070` §7.1).
 *
 * **An inline ATOM, not a mark.** A wiki link is a self-contained reference; making it an
 * atom means a partial deletion cannot leave half a broken link behind (`031`). Backspace
 * removes the whole thing, which is what a user means.
 *
 * **Exactly two attrs: `pageId` and `label`.** `031` defines the node and this file
 * implements it — do not add a third. A `resolved` or `title` attr would be derived state
 * stored in the document, which is the one thing the link design refuses to do (`070` §7.4).
 */
import { mergeAttributes, Node } from '@tiptap/core';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react';
import { search, type SearchPageHit } from '@/api/endpoints/search';
import type { Breadcrumb } from '@/api/endpoints/pages';
import { WikiLinkMenu, type WikiLinkMenuHandle } from '@/features/editor/WikiLinkMenu';
import { WikiLinkNodeView } from '@/features/editor/WikiLinkNodeView';

/** `070` §7.1: the source is `GET /api/search?q=…&limit=8` — the palette's combined query. */
const LIMIT = 8;
/** Same 150 ms as the palette. The plugin owns the timer (v3) — we do not hand-roll one. */
const DEBOUNCE_MS = 150;

export type WikiLinkItem =
  | { kind: 'page'; id: string; title: string; icon: string | null; breadcrumb: Breadcrumb[] }
  /** THE feature: link to a thing that does not exist yet, and the link becomes the to-do. */
  | { kind: 'create'; label: string };

export const wikiLinkPluginKey = new PluginKey('wikiLinkSuggestion');

/**
 * The rows the menu shows.
 *
 * The last row is **"Create page 'X'"** whenever there is no EXACT, case-insensitive title
 * match — not merely when there are no results. Searching `Grey` may well return five pages
 * and still none of them is called `Grey`, and that is exactly when you want to make it.
 */
export function buildItems(query: string, hits: readonly SearchPageHit[]): WikiLinkItem[] {
  const label = query.trim();

  const items: WikiLinkItem[] = hits.map((hit) => ({
    kind: 'page',
    id: hit.id,
    title: hit.title,
    icon: hit.icon,
    breadcrumb: hit.breadcrumb,
  }));

  if (label === '') return items;

  const exact = hits.some((hit) => hit.title.trim().toLowerCase() === label.toLowerCase());
  if (!exact) items.push({ kind: 'create', label });

  return items;
}

const suggestion: Omit<SuggestionOptions<WikiLinkItem, WikiLinkItem>, 'editor'> = {
  pluginKey: wikiLinkPluginKey,
  char: '[[',
  startOfLine: false,
  // Page titles contain spaces — "The Grey Vault" is the normal case, not the edge case.
  allowSpaces: true,
  // The default (`[' ']`) would refuse to trigger on `foo[[bar`. There is no reason a link
  // may not follow a word character, and a suggester that silently does not open is worse
  // than one that opens when you did not quite mean it (Esc costs one key).
  allowedPrefixes: null,
  // v3 debounces `items()` itself AND hands us an `AbortSignal` that it aborts on the next
  // keystroke — so a slow response for `[[ith` can never land on top of `[[ithkar`. Both
  // of the guarantees `070` §4 asks for, from the plugin, with no timer of our own.
  debounce: DEBOUNCE_MS,

  items: async ({ query, signal }): Promise<WikiLinkItem[]> => {
    const label = query.trim();
    if (label === '') return [];

    try {
      const results = await search(label, LIMIT, signal);
      return buildItems(label, results.pages);
    } catch {
      // An aborted or failed search must not kill the menu: offering to create the page is
      // still a perfectly good answer, and it is the one that works offline.
      return buildItems(label, []);
    }
  },

  command: ({ editor, range, props: item }) => {
    const attrs =
      item.kind === 'page'
        ? // The label is the title AT AUTHORING TIME. It is a snapshot, and it is allowed to
          // go stale: the renderer shows the target's current title (`070` §7.5).
          { pageId: item.id, label: item.title }
        : // "Create page 'X'" inserts an UNRESOLVED link and **does not create a page**
          // (`070` §7.1). Creating it is a separate, explicit act — from the backlinks panel
          // or by clicking the broken link. Auto-creating pages from typing produces a
          // notebook full of empty stubs.
          { pageId: null, label: item.label };

    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        { type: 'wikiLink', attrs },
        // A trailing space, so the caret lands after the atom and typing continues normally
        // rather than fighting the node's boundary.
        { type: 'text', text: ' ' },
      ])
      .run();
  },

  render: () => {
    let component: ReactRenderer<WikiLinkMenuHandle> | null = null;
    let unmount: (() => void) | null = null;

    return {
      onStart: (props) => {
        component = new ReactRenderer(WikiLinkMenu, {
          editor: props.editor,
          props: {
            items: props.items,
            loading: props.loading,
            query: props.query,
            command: props.command,
          },
        });
        // v3 mounts and positions the element with Floating UI, anchored to the caret and
        // repositioned on scroll and resize. No tippy.js — it is not in `020-tech-stack.md`.
        unmount = props.mount(component.element);
      },

      onUpdate: (props) => {
        component?.updateProps({
          items: props.items,
          loading: props.loading,
          query: props.query,
          command: props.command,
        });
      },

      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          // The escape hatch (`070` §7.1): dismiss the menu and LEAVE THE LITERAL `[[` TEXT
          // ALONE. Returning true consumes the key so ProseMirror does not also act on it.
          return true;
        }
        return component?.ref?.onKeyDown(props.event) ?? false;
      },

      onExit: () => {
        unmount?.();
        unmount = null;
        component?.destroy();
        component = null;
      },
    };
  },
};

export const WikiLink = Node.create({
  name: 'wikiLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      pageId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-page-id'),
        renderHTML: (attributes) => {
          const pageId = attributes['pageId'] as string | null;
          return pageId === null ? {} : { 'data-page-id': pageId };
        },
      },
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-label') ?? element.textContent,
        renderHTML: (attributes) => ({ 'data-label': String(attributes['label'] ?? '') }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-page-id]' }, { tag: 'a[data-label]' }];
  },

  /**
   * `031`: rendered as `<a data-page-id="…" class="wiki-link">Label</a>`. This is the
   * SERIALIZED form (copy/paste, `getHTML()`); what the user looks at is the node view.
   * No `href` — see `WikiLinkNodeView.tsx`.
   */
  renderHTML({ HTMLAttributes, node }) {
    const broken = node.attrs['pageId'] === null;

    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        class: broken ? 'wiki-link wiki-link--broken' : 'wiki-link',
      }),
      String(node.attrs['label'] ?? ''),
    ];
  },

  renderText({ node }) {
    // `[[label]]` — the same shape the Markdown projection uses (`031`), so a copy out of
    // the editor as plain text round-trips back in through the suggester.
    return `[[${String(node.attrs['label'] ?? '')}]]`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(WikiLinkNodeView);
  },

  addProseMirrorPlugins() {
    return [Suggestion({ editor: this.editor, ...suggestion })];
  },
});
